import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Editor } from '@monaco-editor/react';
import MediaSelector from '../media/MediaSelector';

interface SeoData {
  meta_title?: string;
  meta_description?: string;
  meta_image?: string;
  meta_robots?: string;
  meta_viewport?: string;
  canonical_url?: string;
  keywords?: string;
  structured_data?: any;
  meta_social?: Array<{
    title: string;
    description: string;
    image: string;
    social_network: string;
  }>;
}

interface SeoEditorProps {
  seo: SeoData;
  onChange: (seo: SeoData) => void;
  disabled?: boolean;
}

const SeoEditor: React.FC<SeoEditorProps> = ({ seo, onChange, disabled = false }) => {
  const handleChange = (field: keyof SeoData, value: any) => {
    if (disabled) return;
    onChange({
      ...seo,
      [field]: value
    });
  };

  const handleJsonChange = (value: string | undefined) => {
    if (disabled) return;
    if (value) {
      try {
        const jsonData = JSON.parse(value);
        handleChange('structured_data', jsonData);
      } catch (error) {
        // Invalid JSON, don't update
      }
    }
  };

  const handleSocialMetaChange = (index: number, field: string, value: string) => {
    if (disabled) return;
    const newMetaSocial = [...(seo.meta_social || [])];
    newMetaSocial[index] = { ...newMetaSocial[index], [field]: value };
    handleChange('meta_social', newMetaSocial);
  };

  const addSocialMeta = () => {
    if (disabled) return;
    const newMetaSocial = [...(seo.meta_social || []), {
      title: '',
      description: '',
      image: '',
      social_network: 'Facebook'
    }];
    handleChange('meta_social', newMetaSocial);
  };

  const removeSocialMeta = (index: number) => {
    if (disabled) return;
    const newMetaSocial = [...(seo.meta_social || [])];
    newMetaSocial.splice(index, 1);
    handleChange('meta_social', newMetaSocial);
  };

  return (
    <Box sx={{ maxWidth: 800 }}>
      <TextField
        fullWidth
        label="Meta Title"
        value={seo.meta_title || ''}
        onChange={(e) => handleChange('meta_title', e.target.value)}
        sx={{ mb: 2 }}
        disabled={disabled}
      />
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Meta Description"
        value={seo.meta_description || ''}
        onChange={(e) => handleChange('meta_description', e.target.value)}
        sx={{ mb: 2 }}
        disabled={disabled}
      />
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Meta Image
        </Typography>
        <MediaSelector
          value={seo.meta_image || ''}
          onChange={(value) => handleChange('meta_image', value)}
          fileTypeFilter="images"
          previewSize="medium"
          disabled={disabled}
        />
      </Box>
      <TextField
        fullWidth
        label="Meta Robots"
        value={seo.meta_robots || ''}
        onChange={(e) => handleChange('meta_robots', e.target.value)}
        sx={{ mb: 2 }}
        disabled={disabled}
      />
      <TextField
        fullWidth
        label="Meta Viewport"
        value={seo.meta_viewport || ''}
        onChange={(e) => handleChange('meta_viewport', e.target.value)}
        sx={{ mb: 2 }}
        disabled={disabled}
      />
      <TextField
        fullWidth
        label="Canonical URL"
        value={seo.canonical_url || ''}
        onChange={(e) => handleChange('canonical_url', e.target.value)}
        sx={{ mb: 2 }}
        disabled={disabled}
      />
      <TextField
        fullWidth
        label="Keywords"
        value={seo.keywords || ''}
        onChange={(e) => handleChange('keywords', e.target.value)}
        sx={{ mb: 2 }}
        disabled={disabled}
      />

      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Social Media Meta Tags</Typography>
      {(seo.meta_social || []).map((social, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Typography variant="subtitle1">Social Network {index + 1}</Typography>
            {!disabled && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => removeSocialMeta(index)}
              >
                Remove
              </Button>
            )}
          </Box>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Social Network</InputLabel>
            <Select
              value={social.social_network || ''}
              label="Social Network"
              onChange={(e) => handleSocialMetaChange(index, 'social_network', e.target.value)}
              disabled={disabled}
            >
              <MenuItem value="Facebook">Facebook</MenuItem>
              <MenuItem value="Twitter">Twitter</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Title"
            value={social.title || ''}
            onChange={(e) => handleSocialMetaChange(index, 'title', e.target.value)}
            sx={{ mb: 2 }}
            disabled={disabled}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Description"
            value={social.description || ''}
            onChange={(e) => handleSocialMetaChange(index, 'description', e.target.value)}
            sx={{ mb: 2 }}
            disabled={disabled}
          />
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Image
            </Typography>
            <MediaSelector
              value={social.image || ''}
              onChange={(value) => handleSocialMetaChange(index, 'image', value)}
              fileTypeFilter="images"
              previewSize="medium"
              disabled={disabled}
            />
          </Box>
        </Paper>
      ))}
      {!disabled && (
        <Button
          variant="outlined"
          onClick={addSocialMeta}
          sx={{ mt: 2 }}
        >
          Add Social Network
        </Button>
      )}

      <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Structured Data (JSON-LD)</Typography>
      <Box sx={{ height: 400, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Editor
          height="100%"
          defaultLanguage="json"
          value={JSON.stringify(seo.structured_data || {}, null, 2)}
          onChange={handleJsonChange}
          options={{
            minimap: { enabled: false },
            formatOnPaste: true,
            formatOnType: true,
            automaticLayout: true,
            scrollBeyondLastLine: false,
            fontSize: 14,
            lineNumbers: 'on',
            folding: true,
            wordWrap: 'on',
            readOnly: disabled
          }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Enter valid JSON-LD structured data for SEO. This will be added to the page's HTML.
      </Typography>
    </Box>
  );
};

export default SeoEditor; 